import {
  PersistedFormGroup,
  PersistedFormArray,
  PersistedArray,
} from './persisted-form-group';
import type {
  PersistedFormGroupOptions,
  PersistedFormArrayOptions,
  PersistedArrayOptions,
} from './persisted-form-group';
import type {
  SchemaFormControl,
  SchemaFormGroup,
} from '@apexdesigner/schema-forms';
import { Match } from './match';
export { Match };
import { matchSchema } from '@schemas/business-objects/match';
import { GroupMatchEntryFormArray } from './group-match-entry-form-group';
import { HorseFormGroup } from './horse-form-group';
import { MatchResultFormArray } from './match-result-form-group';
import { UserFormGroup } from './user-form-group';
import type { PdfRenderResult } from '../interface-definitions/index';

export class MatchFormGroup extends PersistedFormGroup {
  declare controls: {
    id: SchemaFormControl;
    mareMeasurements: SchemaFormGroup;
    stallionMeasurements: SchemaFormGroup;
    mareSizeInfluence: SchemaFormControl;
    sirePatternInfluence: SchemaFormControl;
    seed: SchemaFormControl;
    calculationVersion: SchemaFormControl;
    runTimestamp: SchemaFormControl;
    legacyMasterId: SchemaFormControl;
    mareId: SchemaFormControl;
    mare: HorseFormGroup;
    stallionId: SchemaFormControl;
    stallion: HorseFormGroup;
    runById: SchemaFormControl;
    runBy: UserFormGroup;
    results: MatchResultFormArray;
    groupMatchEntries: GroupMatchEntryFormArray;
  };

  declare value: Partial<Match>;

  constructor(
    data?: Record<string, any> | null,
    options?: PersistedFormGroupOptions,
  ) {
    super(matchSchema, Match, data, options);
  }

  get object(): Match {
    return new Match(this.getRawValue());
  }

  protected override createControl(name: string) {
    switch (name) {
      case 'mare':
        return new HorseFormGroup();
      case 'stallion':
        return new HorseFormGroup();
      case 'runBy':
        return new UserFormGroup();
      case 'results':
        return new MatchResultFormArray();
      case 'groupMatchEntries':
        return new GroupMatchEntryFormArray();
      default:
        return undefined;
    }
  }

  static async find(filter?: any): Promise<Match[]> {
    return Match.find(filter);
  }

  static async findOne(filter?: any): Promise<Match | null> {
    return Match.findOne(filter);
  }

  static async findById(id: number, filter?: any): Promise<Match> {
    return Match.findById(id, filter);
  }

  static async create(data: Partial<Match>): Promise<Match> {
    return Match.create(data as any);
  }

  static async updateById(id: number, updates: Partial<Match>): Promise<Match> {
    return Match.updateById(id, updates as any);
  }

  static async deleteById(id: number): Promise<boolean> {
    return Match.deleteById(id);
  }

  static async countMatching(where?: Record<string, any>): Promise<number> {
    return Match.countMatching(where);
  }

  async reportPdf(): Promise<PdfRenderResult> {
    const instance = new Match(this.getRawValue());
    return instance.reportPdf();
  }

  static async run(
    mareId: number,
    stallionId: number,
    mareSizeInfluence: number,
    sirePatternInfluence: number,
    seed?: number,
  ): Promise<MatchFormGroup> {
    const result = await Match.run(
      mareId,
      stallionId,
      mareSizeInfluence,
      sirePatternInfluence,
      seed,
    );
    const fg = new MatchFormGroup();
    fg._populate(result);
    return fg;
  }
}

export class MatchFormArray extends PersistedFormArray {
  declare controls: MatchFormGroup[];

  readonly entityName = 'Match' as const;

  constructor(options?: PersistedFormArrayOptions) {
    super(matchSchema, Match, options);
  }

  protected override createItemGroup() {
    return new MatchFormGroup();
  }

  get array(): Match[] {
    return this.controls.map((group: MatchFormGroup) => group.object);
  }
}

export class MatchPersistedArray extends PersistedArray<Match> {
  readonly entityName = 'Match' as const;

  constructor(options?: PersistedArrayOptions) {
    super(Match, options);
  }
}
